import { useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { Screen } from '~/components/Screen';
import { PrimaryButton } from '~/components/ui';
import { VehicleGlassPlan } from '~/components/VehicleGlassPlan';
import type { GlassArea } from '~/lib/api';
import { AREA_LABELS } from '~/lib/areas';
import type { DriverSide } from '~/lib/handedness';
import { useTheme } from '~/theme/ThemeProvider';

export interface WhichGlassScreenProps {
  reference: string;
  /** Selected areas in selection order — order drives the badges and the shoot order. */
  selected: GlassArea[];
  onChange: (areas: GlassArea[]) => void;
  side: DriverSide | null;
  onSideChange: (side: DriverSide) => void;
  onContinue: () => void;
  onBack: () => void;
}

/**
 * Step 2: which glass is being assessed.
 *
 * Replaces nine enum chips with a plan view of the vehicle, because the operator is
 * standing at the car and "driver quarter glass" is a thing you point at, not a phrase
 * you scan a list for.
 */
export function WhichGlassScreen({
  reference,
  selected,
  onChange,
  side,
  onSideChange,
  onContinue,
  onBack,
}: WhichGlassScreenProps) {
  const { c, t, radius, space, touch } = useTheme();
  const [seenGate] = useState(side !== null);

  const toggle = (area: GlassArea) => {
    onChange(selected.includes(area) ? selected.filter((a) => a !== area) : [...selected, area]);
  };

  const armed = selected.length > 0;

  return (
    <Screen>
      <View style={{ paddingHorizontal: space.md, paddingBottom: space.sm }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.md }}>
          <Pressable
            onPress={onBack}
            accessibilityRole="button"
            accessibilityLabel="Back"
            hitSlop={12}
            // Sized, not just hit-slopped: the glyph's own box is ~14 pt, so without this
            // the visible target and the 44 pt rule disagree with each other.
            style={{
              width: touch.min,
              height: touch.min,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>‹</Text>
          </Pressable>
          <Text style={[t.bodyLg, { fontWeight: '600', color: c.text }]}>Which glass</Text>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: space.md,
          paddingBottom: space.lg,
          gap: space.md,
        }}
      >
        {/*
          The handedness gate. NOT implicit and NOT defaulted: "driver front" is a
          different physical window in an RHD market than an LHD one, so a diagram that
          guesses wrong mislabels the evidence and nothing downstream can detect it.
        */}
        <View
          style={{
            backgroundColor: c.surface,
            borderRadius: radius.lg,
            borderWidth: 1,
            borderColor: c.border,
            padding: space.md,
            gap: space.sm,
          }}
        >
          <Text style={[t.label, { color: c.text }]}>Where does the driver sit?</Text>
          <View
            // The pair is one choice, so it is announced as one group with two options
            // rather than as two unrelated buttons that happen to sit together.
            accessibilityRole="radiogroup"
            style={{ flexDirection: 'row', gap: space.sm }}
          >
            {(['right', 'left'] as DriverSide[]).map((s) => (
              <Pressable
                key={s}
                testID={`driver-side-${s}`}
                accessibilityRole="button"
                accessibilityState={{ selected: side === s }}
                onPress={() => onSideChange(s)}
                style={{
                  flex: 1,
                  minHeight: touch.min,
                  borderRadius: radius.md,
                  borderWidth: side === s ? 0 : 1,
                  borderColor: c.border2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: side === s ? c.primary : c.surface,
                }}
              >
                <Text style={[t.label, { color: side === s ? c.onPrimary : c.text }]}>
                  {s === 'right' ? 'Right' : 'Left'}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        <VehicleGlassPlan side={side} selected={selected} onToggle={toggle} />

        {selected.length > 0 ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.sm }}>
            {selected.map((area, i) => (
              <Pressable
                key={area}
                testID={`remove-area-${area}`}
                accessibilityRole="button"
                accessibilityLabel={`Remove ${AREA_LABELS[area]}`}
                onPress={() => toggle(area)}
                // 32 tall reads as a chip, so the 44 pt target is bought with hitSlop
                // rather than by growing the pill past the design system's 14/32.
                hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: space.sm,
                  backgroundColor: c.brandSoft,
                  borderRadius: radius.pill,
                  height: 32,
                  paddingLeft: space.md,
                  paddingRight: space.xs,
                }}
              >
                <Text style={[t.label, { color: c.text }]}>{String(i + 1)}</Text>
                <Text style={[t.label, { color: c.text }]}>{AREA_LABELS[area]}</Text>
                <Text style={[t.label, { color: c.text, width: 24, textAlign: 'center' }]}>×</Text>
              </Pressable>
            ))}
          </View>
        ) : null}

        {!seenGate && side === null ? (
          <Text style={[t.body, { color: c.dim }]}>
            The windscreen, roof and rear screen can be picked either way — they are the same window
            in every market.
          </Text>
        ) : null}
      </ScrollView>

      <View style={{ paddingHorizontal: space.md, paddingBottom: space.md, gap: space.sm }}>
        <PrimaryButton
          testID="continue-to-shoot"
          label={
            armed
              ? `Photograph ${selected.length} area${selected.length > 1 ? 's' : ''}`
              : 'Pick at least one area'
          }
          disabled={!armed}
          onPress={onContinue}
        />
      </View>
    </Screen>
  );
}
